import { Capacitor } from '@capacitor/core';

/**
 * src/api/harperClient.ts — REST client for Harper Fabric-deployed Resources.
 *
 * Bearer-token auth. Access token comes from an injected provider (set by
 * ApiClientSetup.tsx from AuthContext). On a 401, we invoke the registered
 * unauthorized handler — which triggers a silent refresh and returns a fresh
 * access token — then retry the original request once. If the retry also
 * 401s (or the handler returns null), the caller sees the error and
 * AuthContext has already transitioned to `session_expired`.
 *
 * Envelope handling mirrors the server contract:
 *   - Collection GET  → { data: T[], total, customers? }
 *   - Single GET      → bare T
 *   - Custom POST/PUT → whatever the Resource returns
 */

// -- Config ------------------------------------------------------------------

/**
 * Default to the origin the SPA was served from so the app works under any
 * hostname Harper serves us at (dev.attic.harperfabric.com, app.attic.it.com,
 * etc.). VITE_HARPER_URL still overrides for local dev against a remote
 * cluster. The hardcoded string is a build-time fallback for environments
 * where `window` isn't defined (SSR, certain test contexts).
 *
 * Capacitor native shells load from `https://localhost` (the WebView's local
 * server). That origin is not a Harper backend, so requests to it return
 * the SPA fallback instead of API responses. Detect native platform and
 * pin to the production Harper-fronted domain.
 */
function defaultHarperBaseUrl(): string {
  if (Capacitor.isNativePlatform()) {
    return 'https://app.attic.it.com';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://dev.attic.harperfabric.com';
}

export const HARPER_BASE_URL =
  (import.meta.env.VITE_HARPER_URL as string | undefined) ??
  defaultHarperBaseUrl();

// -- Auth wiring -------------------------------------------------------------

type AccessTokenProvider = () => string | null;

/**
 * Called when a non-anonymous request returns 401. Implementations should
 * attempt to silently refresh the access token and return the new token,
 * or return null if refresh is impossible (no refresh token, refresh
 * expired, etc.). Returning null is what signals the caller to give up
 * and surface the original 401 to the UI.
 */
type UnauthorizedHandler = () => Promise<string | null>;

let _accessTokenProvider: AccessTokenProvider = () => null;
let _onUnauthorized: UnauthorizedHandler | null = null;

/**
 * Wire up how the client reads the current access token. AuthContext calls
 * this once at boot with a getter that reads its state.
 */
export function configureHarperClient(provider: AccessTokenProvider): void {
  _accessTokenProvider = provider;
}

/**
 * Register the 401 refresh handler. Called once at app boot by
 * ApiClientSetup, which wires it to AuthContext's refresh flow.
 */
export function configureOnUnauthorized(handler: UnauthorizedHandler | null): void {
  _onUnauthorized = handler;
}

// -- Errors ------------------------------------------------------------------

export class HarperError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'HarperError';
    this.status = status;
  }
}

// -- Core request ------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | undefined;
  body?: unknown;
  signal?: AbortSignal | undefined;
  /**
   * Skip the Authorization header and the refresh-on-401 retry. Used by
   * anonymous endpoints (AuthLogin, AuthRefresh, ErrorLog) where the 401
   * response means "bad credentials," not "expired session," so retry would
   * be wrong.
   */
  anonymous?: boolean | undefined;
}

async function doFetch(path: string, opts: RequestOptions, accessToken: string | null): Promise<Response> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken && !opts.anonymous) headers['Authorization'] = `Bearer ${accessToken}`;

  const init: RequestInit = { method, headers };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;
  return fetch(`${HARPER_BASE_URL}${path}`, init);
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }

  if (!res.ok) {
    let msg: string;
    if (typeof parsed === 'string') {
      msg = parsed.replace(/^Error:\s*/, '');
    } else if (parsed && typeof parsed === 'object' && 'message' in parsed) {
      msg = String((parsed as { message: unknown }).message);
    } else if (parsed && typeof parsed === 'object' && 'title' in parsed) {
      // Harper's default 401/403 envelope uses { title, status, instance }
      msg = String((parsed as { title: unknown }).title);
    } else {
      msg = res.statusText || `HTTP ${res.status}`;
    }
    throw new HarperError(res.status, msg);
  }

  return parsed as T;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const anonymous = !!opts.anonymous;
  const initialToken = anonymous ? null : _accessTokenProvider();

  // Short-circuit: a non-anonymous call with no access token in hand means
  // we're signed out. Throw immediately rather than making a request that
  // will 401. Callers render offline/loading states accordingly; the 401
  // handler isn't invoked (no session to expire — we already know there's
  // none).
  if (!anonymous && !initialToken) {
    throw new HarperError(401, 'Not authenticated');
  }

  let res = await doFetch(path, opts, initialToken);

  // Silent refresh-and-retry: a single attempt to recover from an expired
  // access token. The handler returns the new token on success or null on
  // failure (no refresh token / refresh rejected). Anonymous calls skip this
  // path entirely — a 401 there means bad user-entered creds and must not
  // trigger a refresh.
  if (!anonymous && res.status === 401 && _onUnauthorized) {
    const newToken = await _onUnauthorized();
    if (newToken) {
      res = await doFetch(path, opts, newToken);
    }
  }

  return parseResponse<T>(res);
}

// -- Normalization -----------------------------------------------------------
// Harper PKs are `uuid`; the frontend's historical Entity types use `id`.
// Mirror apiClient.ts's behavior so downstream code keeps working during the
// migration. `qr_code` fallback preserves CrateCard rendering for crates seeded
// before the qr_code field existed.

function normalize<T>(record: T): T {
  if (!record || typeof record !== 'object') return record;
  const rec = record as Record<string, unknown>;
  if (rec.uuid && !rec.id) rec.id = rec.uuid;
  if (!rec.qr_code && rec.uuid) rec.qr_code = rec.uuid;
  return rec as T;
}

function normalizeArray<T>(arr: T[]): T[] {
  return Array.isArray(arr) ? arr.map(normalize) : arr;
}

// -- Response types ----------------------------------------------------------

export interface Collection<T> {
  data: T[];
  total: number;
  /** Admin* Resources attach a customer lookup map alongside the data. */
  customers?: Record<string, { name: string | null; email: string | null }>;
}

// -- Request helpers ---------------------------------------------------------

export async function getCollection<T>(path: string, signal?: AbortSignal): Promise<Collection<T>> {
  const res = await request<Collection<T>>(path, { signal });
  return { ...res, data: normalizeArray(res.data ?? []) };
}

export async function getOne<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  try {
    const res = await request<T>(path, { signal });
    return normalize(res);
  } catch (err) {
    if (err instanceof HarperError && err.status === 404) return null;
    throw err;
  }
}

export async function post<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
  opts?: { anonymous?: boolean },
): Promise<T> {
  return request<T>(path, { method: 'POST', body, signal, anonymous: opts?.anonymous });
}

export async function put<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: 'PUT', body, signal });
}

export async function patch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: 'PATCH', body, signal });
}

export async function del<T = void>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: 'DELETE', signal });
}

// -- Record-level CRUD (for auto-gen @export tables + custom Resources) ------

export async function createRecord(
  resource: string,
  record: { uuid: string } & Record<string, unknown>,
): Promise<void> {
  await put<void>(`/${resource}/${record.uuid}`, record);
}

export async function updateRecord<T = unknown>(
  resource: string,
  id: string,
  partial: Record<string, unknown>,
): Promise<T> {
  return put<T>(`/${resource}/${id}`, { uuid: id, ...partial });
}

export async function deleteRecord(resource: string, id: string): Promise<void> {
  await del<void>(`/${resource}/${id}`);
}

/**
 * Client-side-filtered collection query. Mirrors the old Operations API
 * `queryTable(table, { attribute: value })` semantics so existing call sites
 * (rbacGuard, searchApi, crateQueries) keep working unchanged.
 */
export async function queryByAttribute<T>(
  resource: string,
  attribute: string,
  value: unknown,
): Promise<T[]> {
  const res = await getCollection<T>(`/${resource}`);
  return res.data.filter(
    (row) => (row as Record<string, unknown>)[attribute] === value,
  );
}

export async function queryTable<T>(
  resource: string,
  filters?: Record<string, unknown>,
): Promise<T[]> {
  if (!filters || Object.keys(filters).length === 0) {
    const res = await getCollection<T>(`/${resource}`);
    return res.data;
  }
  const first = Object.entries(filters)[0];
  if (!first) {
    const res = await getCollection<T>(`/${resource}`);
    return res.data;
  }
  const [attribute, value] = first;
  return queryByAttribute<T>(resource, attribute, value);
}

export async function getRecord<T>(resource: string, id: string): Promise<T | null> {
  return getOne<T>(`/${resource}/${id}`);
}

export async function insertRecord<T = unknown>(
  resource: string,
  record: Record<string, unknown>,
): Promise<T> {
  const uuid = String(record.uuid ?? crypto.randomUUID());
  await createRecord(resource, { ...record, uuid });
  return { inserted_hashes: [uuid], uuid } as unknown as T;
}

/**
 * Compatibility shim matching the old apiClient.apiFetch signature.
 * Only the patterns the frontend actually uses are supported:
 *   - GET /Resource/<email>   → email-scoped Customer lookup
 *   - GET /Resource/<uuid>    → single-record fetch
 *   - GET /Resource[?k=v]     → collection (optionally filtered client-side)
 *   - POST|PUT /Resource      → insertRecord shim
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const url = new URL(path, 'http://local/');
  const parts = url.pathname.replace(/^\/+/, '').split('/');
  const resource = parts[0];
  const id = parts[1];
  if (!resource) throw new HarperError(400, 'apiFetch: missing resource in path');

  if (method === 'GET') {
    if (id) {
      if (id.includes('@')) {
        const rows = await queryByAttribute<T>(resource, 'email', id);
        return (rows.length > 0 ? rows[0] : null) as T;
      }
      return (await getOne<T>(`/${resource}/${id}`)) as T;
    }
    const filters: Record<string, string> = {};
    url.searchParams.forEach((v, k) => { filters[k] = v; });
    const keys = Object.keys(filters);
    if (keys.length === 0) {
      const res = await getCollection<T>(`/${resource}`);
      return res.data as unknown as T;
    }
    const firstKey = keys[0]!;
    return (await queryByAttribute<T>(resource, firstKey, filters[firstKey] ?? '')) as unknown as T;
  }

  if (method === 'POST' || method === 'PUT') {
    const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
    return insertRecord<T>(resource, body as Record<string, unknown>);
  }

  throw new HarperError(405, `apiFetch shim: unsupported method "${method}"`);
}

/**
 * Legacy apiClient exposed a global abort registry tied to the Operations API
 * fetch pipeline. Basic-auth REST calls here don't share that plumbing yet —
 * kept as a no-op so ProfileScreen and other callers don't need rewiring.
 */
export function abortAll(): void { /* no-op */ }

/**
 * Legacy apiClient exposed a token-refresh status stream. The new refresh
 * happens transparently via configureOnUnauthorized — no subscription
 * surface needed. Kept as a no-op so existing call sites keep compiling.
 */
type RefreshStatusCallback = (event: { type: 'start' | 'success' | 'failure' }) => void;
export function subscribeToRefreshStatus(_cb: RefreshStatusCallback): () => void {
  return () => {};
}
